import ClientStorage from 'ClientStorage';
import { type CurrentUserIguanaObject, getProgramApplication } from 'Users';
import { type CareerProfileIguanaObject } from 'CareerProfiles';
import { type ProgramFamilyFormDatum, getProgramFamilyFormDatum } from 'ProgramFamilyFormData';
import { type FormConfig } from 'FormConfigs';
import { type FormCompletionInput } from './FormCompletionHelper.types';

// The key we write when a user saves a step. It records that the user has visited the step, which
// steps with `requiresVisit` use to decide between a check mark and no mark.
//
// NOTE: the key is not namespaced per form context, so the application and the profile would share
// the visited state for any step name that appears in both step lists. Nothing is broken today,
// because `requiresVisit` is set on student_network_settings only, and that step appears only in
// StudentProfileFormConfigs. Namespacing the key would drop a check mark for every student who has
// already visited that step, so leave it alone until that trade-off is worth making.
const visitedStepStorageKey = (stepName: string) => `saved_${stepName}`;

export const markStepVisited = (stepName: string) => {
    ClientStorage.setItem(visitedStepStorageKey(stepName), true);
};

export const getVisitedSteps = (formConfigs: FormConfig[]): ReadonlySet<string> =>
    new Set(
        formConfigs
            .filter(formConfig => ClientStorage.getItem(visitedStepStorageKey(formConfig.stepName)))
            .map(formConfig => formConfig.stepName),
    );

const getCombinedFormValues = (
    user: CurrentUserIguanaObject,
    careerProfile?: CareerProfileIguanaObject,
    programFamilyFormDatum?: ProgramFamilyFormDatum,
) => {
    // If a careerProfile was not explicitly passed, derive it from the user. The callers that edit a
    // form pass a proxy instead, so completion tracks the unsaved values.
    const _careerProfile = careerProfile || user.career_profile;

    // If a programFamilyFormDatum was not explicitly passed, derive it from the user. As above, the
    // callers that edit a form pass a proxy.
    const _programFamilyFormDatum =
        programFamilyFormDatum ||
        (user.relevantCohort && getProgramFamilyFormDatum(user, user.relevantCohort.programFamily)) ||
        {};

    // The forms in FormConfigs are backed by two data models - CareerProfile and ProgramFamilyFormDatum.
    // Some forms write to only one of those models, while other forms can write to both of them, so in order for us
    // to check completeness against the form's validationSchema, we need to combine them into a single object.
    return {
        ..._careerProfile,
        ..._programFamilyFormDatum,
    } as Record<string, unknown>;
};

/**
 * Builds a FormCompletionInput from the Angular/Iguana world.
 *
 * This is the only part of the FormCompletionHelper module that knows about Iguana models or browser
 * storage. The completion functions themselves are pure. A React form does not need this adapter -
 * it can build the input from its own values.
 */
export const buildFormCompletionInput = (
    user: CurrentUserIguanaObject,
    formConfigs: FormConfig[],
    careerProfile?: CareerProfileIguanaObject,
    programFamilyFormDatum?: ProgramFamilyFormDatum,
): FormCompletionInput => ({
    formValues: getCombinedFormValues(user, careerProfile, programFamilyFormDatum),
    visitedSteps: getVisitedSteps(formConfigs),
    hasSubmittedApplication: !!getProgramApplication(user),
});
